import { vetor, type Vetor3 } from '../geometria/vetor3'
import {
  RECLINACAO_ENCOSTO_RAD,
  RECUO_TRILHO,
  centrosDoBanco,
  idsDoBanco,
  larguraDoBanco,
  medidasDaAncora,
} from './bancos'
import { nomeDe } from './catalogo'
import { estadoDe, type MapaEstados } from './estadoPecas'
import {
  ESPESSURA_PAINEL_TETO,
  secaoColuna,
  xDaEstrutura,
  yFaceInternaDoTeto,
  type PerfilVeiculo,
} from './perfilVeiculo'

/**
 * As paredes e os móveis do habitáculo, como DADO.
 *
 * A engine de física precisa de volumes contra os quais a carga solta bata. Este
 * módulo os descreve em TypeScript puro — sem React, sem Three.js e sem Rapier —
 * por três razões:
 *
 * 1. continua testável no vitest, que é o critério de aceite do resto do domínio;
 * 2. as cotas saem do `PerfilVeiculo`, nunca de números escritos na cena, o mesmo
 *    invariante de `superficiesInternas.ts` — "onde o corpo bate" e "o que está
 *    desenhado ali" têm de ser a mesma geometria;
 * 3. os móveis (bancos, encostos, apoios de cabeça, volante) reaproveitam
 *    `volumesObstaculo()`, que já honra reclinar, recuar e remover. Redescrevê-los
 *    aqui criaria uma segunda verdade sobre onde está o encosto.
 *
 * A casca é cuboide; os móveis são cilindros verticais, que é a forma que
 * `VolumeObstaculo` já descreve (centro no plano, raio, faixa de altura).
 */

/**
 * Espessura das chapas da casca, em metros.
 *
 * Exportada porque o solucionador precisa dela para NÃO fazer o corpo nascer
 * dentro de uma chapa: a ponta do pé nascia 2,6 cm dentro da parede corta-fogo, e
 * só a conta de onde a chapa começa diz onde o pé tem de parar. Uma cópia do
 * número lá acabaria discordando deste.
 */
export const ESPESSURA_CHAPA = 0.04

/** Quanto o forro rouba da meia-largura útil. */
const FOLGA_FORRO = 0.02

/** Profundidade do tampo do painel, medido da parede corta-fogo para dentro. */
const PROFUNDIDADE_TAMPO = 0.3

export interface ColisorCuboide {
  forma: 'cuboide'
  id: string
  nome: string
  /** centro do volume, em coordenadas do veículo */
  centro: Vetor3
  /** meia-extensão em cada eixo, antes da rotação */
  meiaExtensao: Vetor3
  /** rotação em torno de +X, em radianos; só o para-brisa a usa */
  inclinacaoXRad: number
}

export type ColisorHabitaculo = ColisorCuboide

/** Extremo dianteiro do compartimento de passageiros (a parede corta-fogo). */
const zParedeCortaFogo = (perfil: PerfilVeiculo): number => perfil.colunaA.zBase

/**
 * Casca do habitáculo: assoalho, teto, laterais, para-brisa, painel, parede
 * traseira e o fechamento de trás.
 *
 * As chapas ficam POR FORA do volume útil — o assoalho abaixo de `pisoY`, o teto
 * acima da face interna — para que um corpo apoiado no piso não nasça dentro da
 * chapa e seja expulso no primeiro passo da simulação.
 */
export function cascaDoHabitaculo(
  perfil: PerfilVeiculo,
  estados: MapaEstados = {},
): ColisorCuboide[] {
  const meiaLargura = Math.abs(xDaEstrutura(perfil, 1)) - FOLGA_FORRO
  const zFrente = zParedeCortaFogo(perfil)
  const zFundo = perfil.traseiraZ + ESPESSURA_CHAPA
  const meioZ = (zFrente + zFundo) / 2
  const meiaProfundidade = Math.abs(zFrente - zFundo) / 2
  const yTetoInterno = yFaceInternaDoTeto(perfil)
  const meiaAlturaLateral = (yTetoInterno - perfil.pisoY) / 2

  // o para-brisa vai da base da coluna A, na linha de cintura, ao topo dela, na
  // linha do teto: uma chapa inclinada, e a inclinação é o que faz o corpo
  // deslizar para cima em vez de parar seco
  const dz = perfil.colunaA.zTopo - perfil.colunaA.zBase
  const dy = perfil.tetoY - perfil.cinturaY
  const comprimentoParaBrisa = Math.hypot(dy, dz)

  return [
    {
      forma: 'cuboide',
      id: 'assoalho',
      nome: 'Assoalho',
      centro: vetor(0, perfil.pisoY - ESPESSURA_CHAPA / 2, meioZ),
      meiaExtensao: vetor(meiaLargura, ESPESSURA_CHAPA / 2, meiaProfundidade),
      inclinacaoXRad: 0,
    },
    {
      forma: 'cuboide',
      id: 'teto',
      nome: 'Teto',
      centro: vetor(0, yTetoInterno + ESPESSURA_PAINEL_TETO / 2, meioZ),
      meiaExtensao: vetor(meiaLargura, ESPESSURA_PAINEL_TETO / 2, meiaProfundidade),
      inclinacaoXRad: 0,
    },
    ...([1, -1] as const).flatMap((sinal) => molduraLateral(perfil, sinal, estados)),
    {
      forma: 'cuboide',
      id: 'paraBrisa',
      nome: 'Para-brisa',
      centro: vetor(
        0,
        (perfil.cinturaY + perfil.tetoY) / 2,
        (perfil.colunaA.zBase + perfil.colunaA.zTopo) / 2,
      ),
      meiaExtensao: vetor(meiaLargura, ESPESSURA_CHAPA / 2, comprimentoParaBrisa / 2),
      // gira o eixo local +Z para a direção da inclinação (0, dy, dz)
      inclinacaoXRad: Math.atan2(dy, dz),
      },
    {
      forma: 'cuboide',
      id: 'paredeCortaFogo',
      nome: 'Parede corta-fogo',
      centro: vetor(
        0,
        (perfil.pisoY + perfil.cinturaY) / 2,
        zFrente - ESPESSURA_CHAPA / 2,
      ),
      meiaExtensao: vetor(
        meiaLargura,
        (perfil.cinturaY - perfil.pisoY) / 2,
        ESPESSURA_CHAPA / 2,
      ),
      inclinacaoXRad: 0,
    },
    {
      // sem esta chapa o compartimento "sobre o painel" não tem no que se
      // apoiar: o objeto nascia no ar e caía direto no assoalho
      forma: 'cuboide',
      id: 'tampoPainel',
      nome: 'Tampo do painel',
      centro: vetor(
        0,
        perfil.cinturaY - ESPESSURA_CHAPA / 2,
        zFrente - PROFUNDIDADE_TAMPO / 2,
      ),
      meiaExtensao: vetor(meiaLargura, ESPESSURA_CHAPA / 2, PROFUNDIDADE_TAMPO / 2),
      inclinacaoXRad: 0,
    },
    {
      forma: 'cuboide',
      id: 'fechamentoTraseiro',
      nome: 'Fechamento traseiro',
      centro: vetor(0, perfil.pisoY + meiaAlturaLateral, zFundo - ESPESSURA_CHAPA / 2),
      meiaExtensao: vetor(meiaLargura, meiaAlturaLateral, ESPESSURA_CHAPA / 2),
      inclinacaoXRad: 0,
    },
  ]
}

/**
 * Móveis do interior: assentos, encostos, apoios de cabeça e volante.
 *
 * Montados a partir de `bancos.ts` — as MESMAS medidas e os MESMOS centros que a
 * cena desenha, incluindo a dobradiça do encosto e o recuo do trilho —, com o
 * estado das peças decidindo o que existe e onde está. Reclinar um encosto muda
 * ao mesmo tempo a folga da rota de extração e o que a carga solta encontra na
 * cinemática: uma peça, um estado, dois efeitos.
 *
 * Não reusa `volumesObstaculo()`, ainda que aquilo descreva os mesmos móveis, e a
 * razão é geométrica: o `raio` de `VolumeObstaculo` é uma MEIA-LARGURA LATERAL,
 * usada para medir distância no plano até o eixo da rota. Tomá-lo como raio de um
 * cilindro dá ao encosto do banco traseiro 60 cm de profundidade, e ele passa a
 * ocupar o porta-malas inteiro — a carga nascia encostada nele e nunca escorregava.
 */
export function moveisDoHabitaculo(
  perfil: PerfilVeiculo,
  estados: MapaEstados = {},
): ColisorCuboide[] {
  const saida: ColisorCuboide[] = []

  for (const ancora of perfil.ancoras) {
    const m = medidasDaAncora(ancora)
    const ids = idsDoBanco(ancora)
    const largura = larguraDoBanco(perfil, ancora)
    const conjuntoRemovido = estadoDe(estados, ids.conjunto) === 'removida'
    const visivel = (id: string) => !conjuntoRemovido && estadoDe(estados, id) !== 'removida'

    const reclinado = estadoDe(estados, ids.encosto) === 'aberta'
    const centros = centrosDoBanco(ancora, reclinado)
    // mesma composição de ângulo que `centrosDoBanco` usa para girar as peças
    const anguloEncosto = -m.encosto.inclinacaoRad + (reclinado ? RECLINACAO_ENCOSTO_RAD : 0)

    if (visivel(ids.assento)) {
      const recuado = estadoDe(estados, ids.assento) === 'afastada'
      saida.push({
        forma: 'cuboide',
        id: ids.assento,
        nome: nomeDe(ids.assento),
        centro: vetor(
          centros.assento.x,
          // `alturaAssento` é o TOPO do assento — é nele que o manequim se apoia.
          // Centrar a almofada ali punha a superfície 5 cm acima do que a pelve
          // do ocupante assume, e o corpo dinâmico nascia dentro dela
          centros.assento.y - m.assento.altura / 2,
          centros.assento.z - (recuado ? RECUO_TRILHO : 0),
        ),
        meiaExtensao: vetor(largura / 2, m.assento.altura / 2, m.assento.profundidade / 2),
        inclinacaoXRad: 0,
      })
    }

    // o apoio de cabeça é montado no encosto: se o encosto sai, ele vai junto
    const encostoPresente = visivel(ids.encosto)

    if (encostoPresente) {
      saida.push({
        forma: 'cuboide',
        id: ids.encosto,
        nome: nomeDe(ids.encosto),
        centro: centros.encosto,
        meiaExtensao: vetor(largura / 2, m.encosto.altura / 2, m.encosto.espessura / 2),
        inclinacaoXRad: -anguloEncosto,
      })
    }

    if (encostoPresente && visivel(ids.apoioCabeca)) {
      saida.push({
        forma: 'cuboide',
        id: ids.apoioCabeca,
        nome: nomeDe(ids.apoioCabeca),
        centro: centros.apoioCabeca,
        meiaExtensao: vetor(
          m.apoioCabeca.largura / 2,
          m.apoioCabeca.altura / 2,
          m.apoioCabeca.espessura / 2,
        ),
        inclinacaoXRad: -anguloEncosto,
      })
    }
  }

  if (estadoDe(estados, 'volante') !== 'removida') {
    const motorista = perfil.ancoras.find((a) => a.id === 'motorista')
    if (motorista) {
      saida.push({
        forma: 'cuboide',
        id: 'volante',
        nome: nomeDe('volante'),
        centro: vetor(motorista.posicao.x, perfil.cinturaY - 0.14, perfil.colunaA.zBase - 0.36),
        meiaExtensao: vetor(0.18, 0.18, 0.04),
        inclinacaoXRad: 0,
      })
    }
  }

  const motorista = perfil.ancoras.find((ancora) => ancora.id === 'motorista')
  if (motorista) {
    const zPedais = zParedeCortaFogo(perfil) - 0.1
    for (const [id, nome, deslocamentoX] of [
      ['pedalFreio', 'Pedal de freio', -0.065],
      ['pedalAcelerador', 'Pedal do acelerador', 0.075],
    ] as const) {
      saida.push({
        forma: 'cuboide',
        id,
        nome,
        centro: vetor(motorista.posicao.x + deslocamentoX, perfil.pisoY + 0.14, zPedais),
        meiaExtensao: vetor(0.05, 0.1, 0.025),
        inclinacaoXRad: -0.28,
      })
    }
  }

  return saida
}

/** Tudo contra o que a carga solta pode bater, no estado atual da maquete. */
export function colisoresDoHabitaculo(
  perfil: PerfilVeiculo,
  estados: MapaEstados = {},
): ColisorHabitaculo[] {
  return [...cascaDoHabitaculo(perfil, estados), ...moveisDoHabitaculo(perfil, estados)]
}

/** Lado da carroceria a que um vão pertence, pelo sinal do eixo X. */
const ladoDoSinal = (sinal: number): 'Esquerda' | 'Direita' => (sinal > 0 ? 'Esquerda' : 'Direita')
const sufixoPorta = (sinal: number): 'Esquerda' | 'Direita' => ladoDoSinal(sinal)

/**
 * Os dois vãos de porta de um lado, com o id da peça de porta correspondente.
 *
 * Os ids batem com os do catálogo (`portaDianteiraEsquerda` e companhia) porque é
 * o estado DESSAS peças que decide se o vão está fechado — a mesma peça que o
 * aluno abre ou remove na aba Maquete.
 */
function vaosDoLado(perfil: PerfilVeiculo, sinal: number) {
  const lado = sufixoPorta(sinal)
  return [
    { id: `portaDianteira${lado}`, nome: `Porta dianteira ${ladoDoSinal(sinal).toLowerCase()}`, faixa: perfil.portaDianteira },
    { id: `portaTraseira${lado}`, nome: `Porta traseira ${ladoDoSinal(sinal).toLowerCase()}`, faixa: perfil.portaTraseira },
  ]
}

/**
 * Lateral do veículo como MOLDURA em torno dos vãos, e não como chapa inteira.
 *
 * Uma chapa inteira torna a ejeção impossível: o corpo sem cinto bate nela e
 * para, enquanto o painel afirma que foi ejetado. Com a moldura, o que fecha o
 * vão é a PORTA — e o estado dela decide. Remover a porta na aba Maquete abre um
 * buraco real por onde o corpo pode sair.
 *
 * Sempre presentes, porque são estrutura: a soleira abaixo dos vãos, a longarina
 * do teto acima deles, e os montantes entre e ao redor. Condicionados ao estado:
 * o painel da porta (da soleira à linha de cintura) e o vidro (da cintura ao
 * teto), que saem quando a peça é aberta, removida ou — no caso do vidro —
 * abaixada.
 */
function molduraLateral(perfil: PerfilVeiculo, sinal: number, estados: MapaEstados = {}): ColisorCuboide[] {
  const meiaLargura = Math.abs(xDaEstrutura(perfil, 1)) - FOLGA_FORRO
  const x = sinal * (meiaLargura + ESPESSURA_CHAPA / 2)
  const lado = ladoDoSinal(sinal)
  const zFrente = zParedeCortaFogo(perfil)
  const zFundo = perfil.traseiraZ + ESPESSURA_CHAPA
  const meioZ = (zFrente + zFundo) / 2
  const meiaProfundidade = Math.abs(zFrente - zFundo) / 2
  const yTeto = yFaceInternaDoTeto(perfil)
  const meiaEspessura = ESPESSURA_CHAPA / 2

  const chapa = (
    id: string,
    nome: string,
    yBaixo: number,
    yAlto: number,
    zA: number,
    zB: number,
  ): ColisorCuboide => ({
    forma: 'cuboide',
    id,
    nome,
    centro: vetor(x, (yBaixo + yAlto) / 2, (zA + zB) / 2),
    meiaExtensao: vetor(meiaEspessura, Math.abs(yAlto - yBaixo) / 2, Math.abs(zB - zA) / 2),
    inclinacaoXRad: 0,
  })

  const saida: ColisorCuboide[] = [
    // soleira: vai de ponta a ponta abaixo dos vãos, e é estrutura
    chapa(`soleira${lado}`, `Soleira ${lado.toLowerCase()}`, perfil.pisoY, perfil.topoSoleiraY, zFrente, zFundo),
    // longarina do teto, acima dos vãos
    chapa(`longarina${lado}`, `Longarina ${lado.toLowerCase()}`, perfil.tetoY - 0.06, yTeto, zFrente, zFundo),
  ]

  const vaos = vaosDoLado(perfil, sinal)
  // os trechos de chapa ENTRE e AO REDOR dos vãos: montantes e painéis fixos
  const limites = [
    zFrente,
    vaos[0]!.faixa.zDianteiro,
    vaos[0]!.faixa.zTraseiro,
    vaos[1]!.faixa.zTraseiro,
    zFundo,
  ]
  for (let i = 0; i < limites.length - 1; i += 1) {
    const zA = limites[i]!
    const zB = limites[i + 1]!
    // os trechos ímpares são os vãos das portas; os pares são chapa fixa
    const ehVao = i === 1 || i === 2
    if (ehVao) continue
    if (Math.abs(zB - zA) < 1e-6) continue
    saida.push(
      chapa(`montante${lado}${i}`, `Estrutura lateral ${lado.toLowerCase()}`, perfil.topoSoleiraY, yTeto, zA, zB),
    )
  }

  // a coluna B fica ENTRE os dois vãos, e os dois se tocam em z: não sobra trecho
  // para ela no fatiamento acima. Entra explícita, porque é estrutura — quem sai
  // pela porta dianteira não atravessa a coluna do meio
  saida.push(
    chapa(
      `colunaB${lado}`,
      `Coluna B ${lado.toLowerCase()}`,
      perfil.topoSoleiraY,
      yTeto,
      perfil.colunaB.z - secaoColuna('B').largura / 2,
      perfil.colunaB.z + secaoColuna('B').largura / 2,
    ),
  )

  for (const vao of vaos) {
    const conjuntoFora = estadoDe(estados, vao.id) === 'removida' || estadoDe(estados, vao.id) === 'aberta'
    if (!conjuntoFora && estadoDe(estados, `${vao.id}.chapaExterna`) !== 'removida') {
      saida.push(
        chapa(vao.id, vao.nome, perfil.topoSoleiraY, perfil.cinturaY, vao.faixa.zDianteiro, vao.faixa.zTraseiro),
      )
    }
    // o vidro some com a porta, e some sozinho quando é abaixado ou retirado
    const vidro = `${vao.id}.vidro`
    if (!conjuntoFora && estadoDe(estados, vidro) !== 'removida' && estadoDe(estados, vidro) !== 'aberta') {
      saida.push(
        chapa(vidro, `${vao.nome} — vidro`, perfil.cinturaY, yTeto, vao.faixa.zDianteiro, vao.faixa.zTraseiro),
      )
    }
  }

  return saida
}
